import { Badge, Flex, Text, Tooltip } from "@chakra-ui/react";
import { formatTime } from "constants/commonFunctions";
import { formatDate } from "constants/commonFunctions";
import { formatDateToDDMONTHNAMEYYYY } from "utils/common";

const ROLE_COLOR_MAP = {
  owner: "green.600",
  landlord: "green.600",
  pg_owner: "green.500",
  agent: "red.600",
  agent_dealer: "red.600",
  dealer: "red.600",
  builder: "orange.600",
  buyer: "purple.600",
  tenant: "teal.600",
  pg_seeker: "pink.600",
  coliving: "yellow.700",
  co_living: "yellow.700",
};
const roleColor = (role) => ROLE_COLOR_MAP[(role || "").toLowerCase()] || "gray.600";

const PLAN_BADGE_SCHEME = {
  "freemium": "gray",
  "pg seeker": "pink",
  "agent": "red",
  "seller": "orange",
  "landlord": "green",
  "pg owner": "teal",
  "buyer": "purple",
  "tenant": "cyan",
  "co-living": "yellow",
  "coliving": "yellow",
};
const planScheme = (planName) => {
  const lower = (planName || "").toLowerCase().trim();
  if (!lower) return "purple";
  if (PLAN_BADGE_SCHEME[lower]) return PLAN_BADGE_SCHEME[lower];
  const family = lower.replace(/\s*(pro plus|pro)\s*$/, "").trim();
  return PLAN_BADGE_SCHEME[family] || "purple";
};

export const userUsageAttemptColumns = [
  {
    Header: "USER",
    accessor: "user_data",
    Cell: ({ value, row }) => {
      const name = `${value?.first_name || ""} ${value?.last_name || ""}`.trim();
      const phone = value?.phone;
      const primaryRole = value?.classified_primary_role || value?.role;
      const platform = value?.platform;
      const planName = row?.original?.plan_name;
      const limitExhausted = value?.contact_view_limit_exhausted;
      const lastActivity = value?.last_activity_at
        ? `${formatDate(value.last_activity_at)}, ${formatTime(value.last_activity_at)}`
        : null;
      if (!name && !phone) return <Text>-</Text>;
      return (
        <Flex
          direction="column"
          align="center"
          justify="center"
          bg={limitExhausted ? "#d4edda" : "transparent"}
          borderRadius="md"
          p={limitExhausted ? 2 : 0}
        >
          <Text as="span">
            {name || "-"}
            {(primaryRole || platform) && (
              <Text as="span">
                {" "}(
                {primaryRole && (
                  <Text as="span" color={roleColor(primaryRole)} fontWeight="600">
                    {primaryRole}
                  </Text>
                )}
                {primaryRole && platform && <Text as="span">, </Text>}
                {platform && (
                  <Text as="span" color="gray.600">
                    {platform}
                  </Text>
                )}
                )
              </Text>
            )}
          </Text>
          {phone && (
            <a href={`/#/admin/users?search_text=${phone}`}>
              <Text fontSize="xs" color="blue.500">{phone}</Text>
            </a>
          )}
          {planName && (
            <Badge
              colorScheme={planScheme(planName)}
              variant="solid"
              borderRadius="md"
              fontSize="0.65rem"
              mt={1}
              px={2}
            >
              {planName}
            </Badge>
          )}
          {lastActivity && (
            <Text fontSize="0.65rem" color="gray.600" mt={1}>
              Last activity: {lastActivity}
            </Text>
          )}
        </Flex>
      );
    },
  },
  {
    Header: "ACTION",
    accessor: "action",
    Cell: ({ value, row }) => {
      const actionType = row?.original?.action_type;
      return (
        <Flex direction="column" align="center">
          <Text>{value || "-"}</Text>
          {actionType && actionType !== value && (
            <Text fontSize="xs" color="gray.500">{actionType}</Text>
          )}
        </Flex>
      );
    },
  },
  {
    Header: "STATUS",
    accessor: "status",
    Cell: ({ value }) => (
      <Badge
        colorScheme={value === "accept" ? "green" : value === "reject" ? "red" : "gray"}
        variant="solid"
        borderRadius="md"
        fontSize="0.7rem"
        px={2}
      >
        {value || "-"}
      </Badge>
    ),
  },
  {
    Header: "ATTEMPTS",
    accessor: "attempt_count",
    Cell: ({ value, row }) => {
      const first = row?.original?.first_attempt_at;
      const count = value || 1;
      return (
        <Flex direction="column" align="center">
          <Badge
            colorScheme={count > 1 ? "orange" : "gray"}
            variant="subtle"
            borderRadius="md"
            fontSize="0.7rem"
            px={2}
          >
            {count}x
          </Badge>
          {count > 1 && first && (
            <Text fontSize="0.6rem" color="gray.500" mt={1}>
              since {formatDate(first)}
            </Text>
          )}
        </Flex>
      );
    },
  },
  {
    Header: "LIMIT / USED",
    accessor: "limit",
    Cell: ({ value, row }) => {
      const used = row?.original?.usage_count;
      if (value == null && used == null) return <Text>-</Text>;
      return <Text>{`${used ?? "-"} / ${value ?? "-"}`}</Text>;
    },
  },
  {
    Header: "OWNER (TARGET)",
    accessor: "owner_data",
    Cell: ({ value }) => {
      const name = `${value?.first_name || ""} ${value?.last_name || ""}`.trim();
      const phone = value?.phone;
      const primaryRole = value?.classified_primary_role || value?.role;
      if (!name && !phone) return <Text>-</Text>;
      return (
        <Flex direction="column" align="center" justify="center">
          <Text as="span">
            {name || "-"}
            {primaryRole && (
              <Text as="span" color={roleColor(primaryRole)} fontWeight="600">
                {" "}({primaryRole})
              </Text>
            )}
          </Text>
          {phone && (
            <a href={`/#/admin/users?search_text=${phone}`}>
              <Text fontSize="xs" color="blue.500">{phone}</Text>
            </a>
          )}
        </Flex>
      );
    },
  },
  {
    Header: "AD",
    accessor: "ad_slug",
    Cell: ({ value, row }) => {
      const adId = row?.original?.ad_id;
      const adRef = row?.original?.ad_ref;
      if (!value && !adId) return <Text>-</Text>;
      if (value) {
        const href = `${process.env.REACT_APP_HEXAHOME_BASE_URL || "https://www.hexahome.in"}/property-details/${value}/`;
        return (
          <Tooltip label={value} aria-label="slug">
            <a href={href} target="_blank" rel="noreferrer" style={{ color: "#3182ce" }}>
              <Text fontSize="xs" noOfLines={1} maxW="200px">
                View {adRef ? `(${adRef})` : "ad"}
              </Text>
            </a>
          </Tooltip>
        );
      }
      return (
        <Tooltip label={adId} aria-label="ad id">
          <Text fontSize="xs" color="gray.500">{adRef || "ad gone"}</Text>
        </Tooltip>
      );
    },
  },
  {
    Header: "ROUTE",
    accessor: "route",
    Cell: ({ value }) => <Text fontSize="xs">{value || "-"}</Text>,
  },
  {
    Header: "IP",
    accessor: "ip",
    Cell: ({ value }) =>
      value ? (
        <a
          href={`https://whatismyipaddress.com/ip/${value}`}
          target="_blank"
          rel="noreferrer"
          style={{ color: "#3182ce", textDecoration: "underline" }}
        >
          {value}
        </a>
      ) : (
        <Text>-</Text>
      ),
  },
  {
    Header: "ATTEMPTED AT",
    accessor: "created_at",
    Cell: ({ value }) => (
      <Text>
        {formatDateToDDMONTHNAMEYYYY(value)} {formatTime(value)}
      </Text>
    ),
  },
  {
    Header: "COMMENT",
    accessor: "comment",
    Cell: ({ value = "" }) => (
      <Flex align="center" justify="center">
        {value?.length > 20 ? (
          <Tooltip label={value} aria-label="comment">
            <Text>{`${value.slice(0, 20)}...`}</Text>
          </Tooltip>
        ) : (
          <Text>{value || "-"}</Text>
        )}
      </Flex>
    ),
  },
  {
    Header: "COMMENT INFO",
    accessor: "commented_by",
    Cell: ({ value, row }) => {
      const commentedAt = row?.original?.commented_at;
      const commentUpdatedAt = row?.original?.comment_updated_at;
      const createdStr = commentedAt
        ? `${formatDate(commentedAt)}, ${formatTime(commentedAt)}`
        : null;
      const updatedStr = commentUpdatedAt
        ? `${formatDate(commentUpdatedAt)}, ${formatTime(commentUpdatedAt)}`
        : null;
      if (!value && !createdStr && !updatedStr) return <Text>-</Text>;
      return (
        <Flex direction="column" align="center" justify="center">
          {value && <Text fontWeight="600">{value}</Text>}
          {createdStr && (
            <Text fontSize="0.65rem" color="gray.600">
              Created: {createdStr}
            </Text>
          )}
          {updatedStr && updatedStr !== createdStr && (
            <Text fontSize="0.65rem" color="gray.600">
              Updated: {updatedStr}
            </Text>
          )}
        </Flex>
      );
    },
  },
];

